import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import TabNavigation from "../Components/Auth/TabNavigation";
import ShopifyWarning from "../Components/Auth/ShopifyWarning";
import ErrorMessage from "../Components/Auth/ErrorMessage";
import SuccessMessage from "../Components/Auth/SuccessMessage";
import LoginForm from "../Components/Auth/LoginForm";
import SignupForm from "../Components/Auth/SignupForm";
import TabSwitchingLinks from "../Components/Auth/TabSwitchingLinks";
import { LoginPartner, SignupPartner } from "../Services/api";
import { ALLOWED_ORIGIN } from "../Global";

const AuthPage = () => {
  const [activeTab, setActiveTab] = useState("login");
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    shopifyStoreLink: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [fromShopify, setFromShopify] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Check URL parameters and Shopify redirect on component mount
  useEffect(() => {
    const tab = searchParams.get("tab");
    const isFromShopify = searchParams.get("from") === "shopify";

    setFromShopify(isFromShopify);

    // Set active tab from URL parameter or default to 'login'
    setActiveTab(tab === "signup" ? "signup" : "login");
  }, [searchParams]);

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email address is invalid";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    // Additional validation for signup
    if (activeTab === "signup") {
      if (!formData.name) {
        newErrors.name = "Name is required";
      }

      if (!formData.shopifyStoreLink) {
        newErrors.shopifyStoreLink = "Shopify store link is required";
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = "Please confirm your password";
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = "Passwords do not match";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle login form submission
  const handleLogin = async (e) => {
    e.preventDefault();

    // Prevent login if not from Shopify
    if (!fromShopify) {
      setErrors({ general: "Please login from your Shopify store." });
      return;
    }

    if (!validateForm()) return;

    setLoading(true);
    setMessage("");

    try {
      const response = await LoginPartner({
        email: formData.email,
        password: formData.password,
      });

      if (response.status === 200 && response.data.token) {
        // Store token in localStorage
        localStorage.setItem("partnerToken", response.data.token);
        setMessage("Login successful!");

        // Check if this is a popup window and send data back to opener
        if (window.opener && window.opener !== window) {
          // Send the response data back to the opener window
          window.opener.postMessage(
            {
              type: "LOGIN_SUCCESS",
              data: response.data,
            },
            ALLOWED_ORIGIN // Use dynamic origin based on environment
          );

          // Close the popup window after a short delay
          setTimeout(() => {
            window.close();
          }, 1000);
        } else {
          // Redirect to dashboard for non-popup scenarios
          setTimeout(() => {
            navigate("/magic");
          }, 1000);
        }
      } else {
        setErrors({
          general: response.data.message || "Invalid email or password",
        });
      }
    } catch (error) {
      setErrors({ general: "An error occurred. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  // Handle signup form submission
  const handleSignUp = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setMessage("");

    try {
      const response = await SignupPartner({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        shopifyStoreLink: formData.shopifyStoreLink,
      });

      if (response.status === 200) {
        setMessage("Account created successfully! Please login.");
        // Switch to login tab and reset message after 3 seconds
        setTimeout(() => {
          setActiveTab("login");
          setFormData((prev) => ({
            ...prev,
            password: "",
            confirmPassword: "",
            name: "",
            shopifyStoreLink: "",
          }));
          setMessage(""); // Reset message after 3 seconds
        }, 3000);
      } else {
        setErrors({
          general: response.data.message || "Failed to create account",
        });
      }
    } catch (error) {
      setErrors({
        general:
          error.response?.data?.message ||
          error.response?.data?.error ||
          "An error occurred. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission based on active tab
  const handleSubmit = (e) => {
    if (activeTab === "login") {
      handleLogin(e);
    } else {
      handleSignUp(e);
    }
  };

  // Change active tab and update URL
  const changeTab = (tab) => {
    setActiveTab(tab);
    // Update URL parameter
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("tab", tab);
    if (fromShopify) {
      newSearchParams.set("from", "shopify");
    }
    navigate({ search: newSearchParams.toString() }, { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div className="text-center">
          <h2 className="mt-2 text-3xl font-bold text-gray-900">
            <span className="text-red-500">Ciqler</span> x{" "}
            <span className="text-green-600">Shopify</span>{" "}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {activeTab === "login"
              ? "Login to your account"
              : "Create a new account, and join Ciqler's Merchant Community!"}
          </p>
        </div>

        {/* Tab Navigation */}
        <TabNavigation activeTab={activeTab} onTabChange={changeTab} />

        {/* Shopify Warning Message */}
        {!fromShopify && activeTab === "login" && <ShopifyWarning />}

        {/* General Error Message */}
        <ErrorMessage message={errors.general} />

        {/* Success Message */}
        <SuccessMessage message={message} />

        {/* Authentication Form */}
        {activeTab === "login" ? (
          <LoginForm
            formData={formData}
            errors={errors}
            loading={loading}
            fromShopify={fromShopify}
            onChange={handleChange}
            onSubmit={handleSubmit}
          />
        ) : (
          <SignupForm
            formData={formData}
            errors={errors}
            loading={loading}
            fromShopify={fromShopify}
            onChange={handleChange}
            onSubmit={handleSubmit}
          />
        )}

        {/* Tab Switching Links */}
        <TabSwitchingLinks activeTab={activeTab} onTabChange={changeTab} />
      </div>
    </div>
  );
};

export default AuthPage;
